import React, { Component } from 'react';
import { connect } from 'react-redux';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import { arrayMove } from 'react-sortable-hoc';
import {
  changeBaseModuleScreen,
  changeBaseModuleLang,
  saveBaseModule,
} from '../../../actions/baseModule';
import {
  getBaseModuleItemsList,
  setCurrentModuleItem,
  setModuleItemsFilterByName,
  setModuleItemsFilterByCategory,
  removeBaseModuleItem,
  togglePublishBaseModuleItem,
  orderBaseModuleItems,
} from '../../../actions/baseModuleItems';
import { getFilteredBaseModuleItemsList, itemListFilterSelectableCategory } from '../../../selectors';
import BaseModuleItemsList from './BaseModuleItemsList';
import BaseModuleCategoryFilter from './BaseModuleCategoryFilter';
import ConfirmationModal from '../../Modals/ConfirmationModal';
import i18n from '../../../internationalization/i18n';
import Spinner from '../../../components/Spinner';

const propTypes = {
  lang: PropTypes.string.isRequired,
  itemsList: PropTypes.array.isRequired,
  itemListFilterByName: PropTypes.string.isRequired,
  itemListFilterByCategory: PropTypes.object,
  changeBaseModuleScreen: PropTypes.func.isRequired,
  changeBaseModuleLang: PropTypes.func.isRequired,
  getBaseModuleItemsList: PropTypes.func.isRequired,
  setCurrentModuleItem: PropTypes.func.isRequired,
  setModuleItemsFilterByName: PropTypes.func.isRequired,
  setModuleItemsFilterByCategory: PropTypes.func.isRequired,
  removeBaseModuleItem: PropTypes.func.isRequired,
  togglePublishBaseModuleItem: PropTypes.func.isRequired,
  selectableItems: PropTypes.array.isRequired,
  orderBaseModuleItems: PropTypes.func.isRequired,
  fetchingItemsList: PropTypes.bool.isRequired,
};

const defaultProps = {
  itemListFilterByCategory: null,
  currentModuleItem: null,
};

class BaseModuleItems extends Component {
  constructor(props) {
    super(props);
    this.state = {
      itemsList: props.itemsList, // temp var for simulate drag and drop on the backend side
      selectedItemId: null,
      showModal: false,
    };

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.modalSubmit = this.modalSubmit.bind(this);
  }

  componentDidMount() {
    this.props.getBaseModuleItemsList();
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props.itemsList, prevProps.itemsList)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ itemsList: this.props.itemsList });
    }
  }

  componentWillUnmount() {
    this.props.setModuleItemsFilterByName('');
    this.props.setModuleItemsFilterByCategory(null);
  }

  onSortEnd = ({ oldIndex, newIndex }) => {
    const { itemsList } = this.state;
    this.setState({
      itemsList: arrayMove(itemsList, oldIndex, newIndex),
    }, () => {
      const orderIds = this.state.itemsList.map(i => i.id);
      this.props.orderBaseModuleItems({ orderIds });
    });
  };

  openModal() {
    this.setState({ showModal: true });
  }

  closeModal() {
    this.setState({ showModal: false });
  }

  modalSubmit() {
    this.setState({ selectedItemId: null, showModal: false }, () => {
      this.props.removeBaseModuleItem()
        .then(() => {
          this.props.setCurrentModuleItem(null);
          this.props.getBaseModuleItemsList();
        });
    });
  }

  handleClickPublish = (routeId, published) => (
    this.props.togglePublishBaseModuleItem(routeId, published)
      .then(() => {
        this.props.getBaseModuleItemsList();
      })
  );

  handleCreateModuleItem = () => (
    this.props.changeBaseModuleScreen('createModuleItem')
  );

  handleFilterByCategory = filterValue => (
    this.props.setModuleItemsFilterByCategory(filterValue)
  );

  handleSelectItem = selectedItemId => (new Promise((resolve) => {
    this.setState({ selectedItemId }, () => {
      const item = this.props.itemsList.find(i => i.id === selectedItemId);
      if (item) {
        resolve(this.props.setCurrentModuleItem(item));
      }
      resolve();
    });
  }));

  handleEditItem = () => {
    this.props.changeBaseModuleScreen('editModuleItem');
  };

  handleChangeFilterByName = (event) => {
    const filter = event.target.value || '';
    this.props.setModuleItemsFilterByName(filter);
  };

  handleChangeLang = (lang) => {
    this.props.changeBaseModuleLang(lang);
  };

  render() {
    const { itemsList, selectedItemId, showModal } = this.state;
    const { itemListFilterByName, itemListFilterByCategory, selectableItems, fetchingItemsList } = this.props;

    return (
      <div className="c-bb-basemodule">
        <div className="c-bb-basemodule-header">
          <div className="c-bb-basemodule-header-icon">
            <i className="icon-password" />
          </div>
          <h1 className="c-bb-basemodule-header-title">
            {i18n.t('CONTENT.baseModuleItem')}
          </h1>
          <span>{i18n.t('BASE_MODULE.language')}: {this.props.lang.toUpperCase()}</span>
        </div>
        <div className="c-bb-basemodule-items-container">
          <div className="c-bb-basemodule-items-header">
            <BaseModuleCategoryFilter
              filterValue={itemListFilterByCategory}
              categories={selectableItems}
              onSelect={this.handleFilterByCategory}
            />
            <div>
              <button
                className="items-header__create-item-btn c-bb-button"
                onClick={this.handleCreateModuleItem}
              >
                <i className="icon-add" />
                {i18n.t('CONTENT.create').toUpperCase()}
              </button>
            </div>
          </div>
          { fetchingItemsList
            ? <Spinner />
            : <BaseModuleItemsList
              itemsList={itemsList}
              selectedItemId={selectedItemId}
              filterByName={itemListFilterByName}
              onSelectItem={this.handleSelectItem}
              onClickEditItem={this.handleEditItem}
              onClickRemoveItem={this.openModal}
              onSortEnd={this.onSortEnd}
              onClickPublish={this.handleClickPublish}
              onChangeFilterByName={this.handleChangeFilterByName}
            />
          }
        </div>
        {showModal &&
          <ConfirmationModal
            onClose={this.closeModal}
            onOk={this.modalSubmit}
            onCancel={this.closeModal}
            title={i18n.t('CONTENT.remove').toUpperCase()}
            submitButtonText={i18n.t('CONTENT.remove').toUpperCase()}
            icon="icon-basic_sheet"
          >
            <div style={{ textAlign: 'center' }}>
              {i18n.t('CONTENT.deleteConfirmation')}
            </div>
          </ConfirmationModal>
        }
      </div>
    );
  }
}

BaseModuleItems.propTypes = propTypes;
BaseModuleItems.defaultProps = defaultProps;

const mapStateToProps = state => ({
  isOpen: state.baseModule.isOpen,
  lang: state.baseModule.lang,
  langs: state.baseModule.langs,
  itemsList: getFilteredBaseModuleItemsList(state),
  currentModuleItem: state.baseModuleItems.currentItem,
  itemListFilterByName: state.baseModuleItems.filterByName,
  itemListFilterByCategory: state.baseModuleItems.filterByCategory,
  selectableItems: itemListFilterSelectableCategory(state),
  fetchingItemsList: state.baseModuleItems.fetchingItemsList,
});

const mapDispatchToProps = {
  changeBaseModuleScreen,
  changeBaseModuleLang,
  saveBaseModule,
  getBaseModuleItemsList,
  setCurrentModuleItem,
  setModuleItemsFilterByName,
  setModuleItemsFilterByCategory,
  removeBaseModuleItem,
  togglePublishBaseModuleItem,
  orderBaseModuleItems,
};

export default withNamespaces()(connect(mapStateToProps, mapDispatchToProps)(BaseModuleItems));



// WEBPACK FOOTER //
// ./src/components/BaseModule/BaseModuleItems/BaseModuleItems.js